import { useChartTheme } from '@/shared/lib/chart-theme';
import type { IncompletePeriods } from '@/shared/lib/incomplete-periods';
import type { Chart, Plugin } from 'chart.js';

/**
 * Settings travel in `options.plugins.incompletePeriods`, not in the plugin
 * object: react-chartjs-2 reads its `plugins` prop once, when the chart is
 * created, but it does apply new options. So a range change on a chart that
 * stays mounted (Levels of Care, Alerts) repaints the right bands.
 */
export type IncompletePeriodsPluginOptions = {
  flags: boolean[];
  noun: 'Week' | 'Month';
  fill: string;
  text: string;
};

declare module 'chart.js' {
  // The type parameter must match chart.js's own declaration to merge.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType> {
    incompletePeriods?: IncompletePeriodsPluginOptions | false;
  }
}

/*
 * Label per the Figma chart (node 655:29005): Work Sans Regular, centered, wrapped
 * onto two lines, sitting 23px below the top of the band. The design's 10px was
 * too small to read (card 196, Anna), so the size is 12px, the same as the
 * design's axis labels.
 */
const fontOf = (size: number) =>
  `400 ${size}px 'Work Sans', ui-sans-serif, system-ui, sans-serif`;
const SIZE = 12;
/** Last resort for very narrow bands (Alerts' 12 months): the design's own 10px. */
const NARROW_SIZE = 10;
const LINE_HEIGHT = 15;
const TOP_GAP = 23;
const SIDE_PADDING = 2;

const getOptions = (chart: Chart): IncompletePeriodsPluginOptions | null => {
  const options = chart.options.plugins?.incompletePeriods;
  // Chart.js types plugin options as Partial; ours are all-or-nothing.
  if (!options || !options.flags?.some(Boolean)) return null;
  return options as IncompletePeriodsPluginOptions;
};

/** Left/right pixel edges of category `i`: halfway to each neighbor. */
const bandEdges = (chart: Chart, i: number, count: number) => {
  const { chartArea, scales } = chart;
  const x = scales.x;
  const center = x.getPixelForValue(i);
  const prev = i > 0 ? x.getPixelForValue(i - 1) : null;
  const next = i < count - 1 ? x.getPixelForValue(i + 1) : null;

  if (prev === null && next === null) {
    return { left: chartArea.left, right: chartArea.right };
  }
  const leftGap = prev === null ? next! - center : center - prev;
  const rightGap = next === null ? center - prev! : next - center;

  return {
    left: Math.max(chartArea.left, center - leftGap / 2),
    right: Math.min(chartArea.right, center + rightGap / 2),
  };
};

/**
 * Longest wording that fits the band: "Incomplete Month" on one line, then on
 * two lines, then "Incomplete" alone, then "Incomplete" at the narrow size, then
 * nothing. Leaves `ctx.font` set to the size it chose.
 */
const fitLines = (
  ctx: CanvasRenderingContext2D,
  noun: string,
  width: number
): string[] => {
  const attempts: [number, string[]][] = [
    [SIZE, [`Incomplete ${noun}`]],
    [SIZE, ['Incomplete', noun]],
    [SIZE, ['Incomplete']],
    [NARROW_SIZE, ['Incomplete']],
  ];
  for (const [size, lines] of attempts) {
    ctx.font = fontOf(size);
    if (lines.every((line) => ctx.measureText(line).width <= width)) {
      return lines;
    }
  }
  return [];
};

export const incompletePeriodPlugin: Plugin = {
  id: 'incompletePeriods',
  beforeDatasetsDraw(chart) {
    const options = getOptions(chart);
    if (!options) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.fillStyle = options.fill;
    options.flags.forEach((flagged, i) => {
      if (!flagged) return;
      const { left, right } = bandEdges(chart, i, options.flags.length);
      ctx.fillRect(left, chartArea.top, right - left, chartArea.height);
    });
    ctx.restore();
  },
  afterDatasetsDraw(chart) {
    const options = getOptions(chart);
    if (!options) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.fillStyle = options.text;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    options.flags.forEach((flagged, i) => {
      if (!flagged) return;
      const { left, right } = bandEdges(chart, i, options.flags.length);
      const lines = fitLines(
        ctx,
        options.noun,
        right - left - SIDE_PADDING * 2
      );
      lines.forEach((line, n) =>
        ctx.fillText(
          line,
          (left + right) / 2,
          chartArea.top + TOP_GAP + n * LINE_HEIGHT
        )
      );
    });
    ctx.restore();
  },
};

/** Value for `options.plugins.incompletePeriods`; `false` switches the plugin off. */
export const useIncompletePeriodsOptions = (
  incompletePeriods?: IncompletePeriods
): IncompletePeriodsPluginOptions | false => {
  const theme = useChartTheme();
  if (!incompletePeriods) return false;

  return {
    flags: incompletePeriods.flags,
    noun: incompletePeriods.granularity === 'week' ? 'Week' : 'Month',
    fill: theme.incompleteFill,
    text: theme.incompleteText,
  };
};
